import { Injectable } from "@angular/core";

import { ApiErrorHelper } from "@core/helpers/api-error.helper";
import { ToasterSeverities } from "@enums/toaster-severities";
import { NotificationStore } from "@features/+shared/notifications/notification.store";
import { NotificationContextService } from "@features/+shared/notifications/notification-context.service";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  constructor(
    private store: NotificationStore,
    private translate: TranslateService,
    private context: NotificationContextService
  ) {}

  /**
   * Raises a notification: a toast in the top-right stack, and a line in the
   * header bell's history.
   *
   * Same signature as before, so its callers did not change. It used to open a
   * MatSnackBar, which shows one message at a time - a second call replaced
   * the first before it could be read - and kept nothing once dismissed.
   * Errors still stay until dismissed; everything else times out after 5s
   * (paused while hovered). See NotificationStore.
   *
   * `params` fills placeholders in the message's title and description, so a
   * notification can name what it is about ("{{name}}"). Where it was raised -
   * page and dialog - is recorded here for every caller.
   */
  show(
    severity: ToasterSeverities,
    message: string,
    information?: unknown,
    params?: Record<string, unknown>
  ): void {
    this.store.push(
      severity,
      this.descriptionKey(message),
      this.resolveInformation(information),
      {
        title: this.titleKey(severity, message),
        params,
        where: this.context.where(),
      }
    );
  }

  /**
   * Every notification reads as a short title with a description under it.
   *
   * The title is `NOTIFY_<KEY>_TITLE` ("Saved", "Not saved"), or a heading
   * per severity when a message has none of its own, so a new caller still
   * gets one. The description is `NOTIFY_<KEY>_DESC` where the original
   * message was too short to explain anything ("Invalid file type"), and
   * otherwise the original message itself - most already are a sentence.
   * Keys, not text, go into the store so the history follows a language
   * switch.
   */
  private titleKey(severity: ToasterSeverities, message: string): string {
    const own = `NOTIFY_${message}_TITLE`;
    return this.hasTranslation(own)
      ? own
      : `NOTIFY_TITLE_${severity.toUpperCase()}`;
  }

  private descriptionKey(message: string): string {
    const own = `NOTIFY_${message}_DESC`;
    return this.hasTranslation(own) ? own : message;
  }

  private hasTranslation(key: string): boolean {
    const text: unknown = this.translate.instant(key);
    return typeof text === "string" && text !== key;
  }

  /**
   * Turns whatever a caller passes as `information` into a clean string.
   * Historically callers passed the raw `HttpErrorResponse.error` here, which
   * rendered as "[object Object]" whenever the backend returned a JSON body.
   * We now normalize any error into a readable message (translating status-code
   * fallbacks), while leaving plain strings untouched for backwards compat.
   */
  private resolveInformation(information: unknown): string | undefined {
    if (information == null) {
      return undefined;
    }

    if (typeof information === "string") {
      return information;
    }

    const { isKey, text } = ApiErrorHelper.resolve(information);

    return isKey ? this.translate.instant(text) : text;
  }
}
